import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CoordFields } from "@/components/network/CoordFields";
import { INFRA_STATUS, STATUS_LABEL, SELECT_CLS, notifyOdcSync } from "@/lib/network";
import { WilayahCombobox } from "@/components/network/WilayahCombobox";

const blank = (kind, refs) => ({
  nama: "", kode: "", wilayah_id: refs?.me?.wilayah_ids?.length === 1 ? refs.me.wilayah_ids[0] : "",
  lokasi_id: refs?.default_lokasi_id || "", alamat: "", latitude: "", longitude: "", status: "AKTIF", catatan: "",
  ...(kind === "odc" ? { kapasitas_splitter: "" } : { odc_id: "", kapasitas_port: 8, tanggal_pasang: "" }),
});

const odcVal = (o) => o.id;
const NO_ODC = "__none__";
const NO_ODC_OPT = { id: NO_ODC, none: true, kode: "", nama: "" };
const odpPrefix = (k) => `ODP-${(k || "").toUpperCase().replace(/[^A-Z0-9]/g, "")}-`;
const odcLabel = (o) => (o.none ? "— Tanpa ODC Induk —" : `${o.kode} — ${o.nama}`);

const KODE_CLS = "font-mono-tab uppercase";
const kodeIn = (setForm) => (e) => setForm((f) => ({ ...f, kode: e.target.value.toUpperCase().replace(/\s/g, "") }));

export function InfraFormDialog({ kind, open, editing, refs, odcList = [], onClose, onSaved }) {
  const [form, setForm] = useState(blank(kind, refs));
  const [saving, setSaving] = useState(false);
  const label = kind === "odc" ? "ODC" : "ODP";
  useEffect(() => {
    if (!open) return;
    if (!editing) return setForm(blank(kind, refs));
    setForm({ ...blank(kind, refs), ...editing, latitude: editing.latitude ?? "", longitude: editing.longitude ?? "",
              kapasitas_splitter: editing.kapasitas_splitter ?? "", ...(kind === "odp" ? { odc_id: editing.odc_id || NO_ODC } : {}), tanggal_pasang: editing.tanggal_pasang || "", catatan: editing.catatan || "" });
  }, [open, editing, kind, refs]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const allWil = refs?.wilayah || [];
  const odcSel = odcList.find((o) => o.id === form.odc_id);
  const wilName = useMemo(() => new Map((refs?.wilayah || []).map((w) => [w.wilayah_id, w.nama_wilayah])), [refs?.wilayah]);
  const odcSub = useCallback((o) => (o.none ? "lepas relasi ODC" : wilName.get(o.wilayah_id) || "Wilayah belum valid"), [wilName]);
  const odcOpts = useMemo(() => [NO_ODC_OPT, ...odcList], [odcList]);
  const wilayahOpts = allWil.filter((w) => (w.status_aktif !== false || w.wilayah_id === form.wilayah_id));
  const wilayahKnown = !form.wilayah_id || allWil.some((w) => w.wilayah_id === form.wilayah_id);
  const odcWilValid = !odcSel || allWil.some((w) => w.wilayah_id === odcSel.wilayah_id);
  const odpMismatch = kind === "odp" && odcSel && odcWilValid && wilayahKnown && form.wilayah_id && odcSel.wilayah_id !== form.wilayah_id;
  const lok = refs?.locations.find((l) => l.id === form.lokasi_id);

  const onOdc = (e) => {
    const odc = odcList.find((o) => o.id === e.target.value);
    if (!odc) return setForm((f) => ({ ...f, odc_id: e.target.value }));
    setForm((f) => ({ ...f, odc_id: e.target.value, wilayah_id: odc.wilayah_id || "", lokasi_id: f.lokasi_id || odc.lokasi_id || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (kind === "odp" && !form.odc_id) return toast.error("Pilih ODC induk atau \"Tanpa ODC Induk\"");
    if (!form.wilayah_id || !wilayahKnown) return toast.error("Wilayah wajib dipilih dari Master Wilayah Billing");
    if (kind === "odp" && !odcWilValid) return toast.error("Wilayah ODC induk belum valid. Perbaiki ODC induk terlebih dahulu.");
    if (odpMismatch) return toast.error("Wilayah ODP harus sama dengan wilayah ODC induk");
    const num = (v) => (v === "" || v === null ? null : Number(v));
    const payload = {
      kode: (form.kode || "").trim(), nama: form.nama, wilayah_id: form.wilayah_id, lokasi_id: form.lokasi_id, alamat: form.alamat,
      latitude: num(form.latitude), longitude: num(form.longitude), status: form.status, catatan: form.catatan,
      ...(kind === "odc" ? { kapasitas_splitter: num(form.kapasitas_splitter) }
        : { odc_id: form.odc_id === NO_ODC ? null : form.odc_id, kapasitas_port: Number(form.kapasitas_port), tanggal_pasang: form.tanggal_pasang || null }),
    };
    setSaving(true);
    try {
      const r = editing ? await api.put(`/network/${kind}/${editing.id}`, payload) : await api.post(`/network/${kind}`, payload);
      toast.success(`${label} ${r.data.kode} tersimpan`);
      notifyOdcSync(r.data.odc_sync, toast);
      onSaved?.(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[calc(100dvh-5.5rem)] overflow-y-auto max-lg:top-[calc(50%+2rem)] lg:max-h-[92vh]" data-testid={`${kind}-form-dialog`}>
        <DialogHeader><DialogTitle className="break-words pr-6">{editing ? `Edit ${label} ${editing.kode}` : `Tambah ${label}`}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Kode {label}</Label>
              <Input value={form.kode || ""} onChange={kodeIn(setForm)} maxLength={40} className={KODE_CLS} data-testid={`${kind}-form-kode`}
                     placeholder={editing ? editing.kode : `Kosongkan = otomatis (${kind === "odc" ? "ODC-xxx" : odcSel ? `${odpPrefix(odcSel.kode)}xx` : form.odc_id === NO_ODC ? "ODP-xxxx" : "ODP-<ODC>-xx"})`}
                     title="Isi manual atau kosongkan untuk kode otomatis. Saat edit, kosong = kode lama tetap." /></div>
            <div><Label>Nama {label}</Label>
              <Input value={form.nama} onChange={set("nama")} required data-testid={`${kind}-form-nama`} /></div>
          </div>
          {refs && !allWil.length && <p className="rounded bg-[#FEF2F2] p-2 text-xs text-[#B91C1C]" data-testid={`${kind}-form-wilayah-empty`}>
            Master Wilayah Billing kosong atau gagal dimuat. Tambahkan wilayah di Billing → Master terlebih dahulu.</p>}
                    {!wilayahKnown && <p className="rounded bg-[#FFFBEB] p-2 text-xs text-[#B45309]" data-testid={`${kind}-form-wilayah-invalid`}>
            Wilayah belum valid (data lama). Pilih ulang wilayah dari Master Wilayah Billing.</p>}
          {kind === "odp" && !odcWilValid && <p className="rounded bg-[#FFFBEB] p-2 text-xs text-[#B45309]" data-testid="odp-form-odc-invalid">
            ODC induk masih memakai wilayah lama yang belum valid. Perbaiki wilayah ODC induk di Master ODC terlebih dahulu.</p>}
          {odpMismatch && <p className="rounded bg-[#FFFBEB] p-2 text-xs text-[#B45309]" data-testid="odp-form-wilayah-mismatch">
            Wilayah ODP berbeda dengan wilayah ODC induk. Pilih ODC induk yang sesuai atau pilih ulang ODC agar wilayah mengikuti ODC.</p>}
          {kind === "odp" && (
            <div><Label>ODC Induk</Label>
              <WilayahCombobox className="w-full" options={odcOpts} value={form.odc_id} testid="odp-form-odc" placeholder="— Pilih ODC —"
                               onChange={(v) => onOdc({ target: { value: v } })} getValue={odcVal} getLabel={odcLabel} getSub={odcSub}
                               searchPlaceholder="Cari kode, nama, atau wilayah ODC…" emptyText="ODC tidak ditemukan." />
              {form.odc_id === NO_ODC && <p className="mt-1 text-xs text-[#6B7280]" data-testid="odp-form-no-odc-hint">
                ODP disimpan tanpa ODC induk. Relasi splitter tidak diubah; hubungkan ODP ke output splitter lewat Master Splitter bila perlu.</p>}</div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Wilayah</Label>
              <WilayahCombobox className="w-full" value={wilayahKnown ? form.wilayah_id : ""} testid={`${kind}-form-wilayah`}
                               onChange={(v) => setForm((f) => ({ ...f, wilayah_id: v }))}
                               disabled={kind === "odp" && !!odcSel && (!odcWilValid || (wilayahKnown && !odpMismatch))}
                               options={kind === "odp" && odcSel ? wilayahOpts.filter((w) => w.wilayah_id === odcSel.wilayah_id || w.wilayah_id === form.wilayah_id) : wilayahOpts} /></div>
            <div><Label>Lokasi</Label>
              <select className={`${SELECT_CLS} w-full`} value={form.lokasi_id} onChange={set("lokasi_id")} required data-testid={`${kind}-form-lokasi`}>
                <option value="">— Pilih —</option>
                {(refs?.locations || []).filter((l) => l.active || l.id === form.lokasi_id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select></div>
            <div><Label>Status</Label>
              <select className={`${SELECT_CLS} w-full`} value={form.status} onChange={set("status")} data-testid={`${kind}-form-status`}>
                {INFRA_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {kind === "odc" ? (
              <div><Label>Kapasitas Splitter (opsional)</Label>
                <Input type="number" min="1" value={form.kapasitas_splitter} onChange={set("kapasitas_splitter")} data-testid="odc-form-splitter" /></div>
            ) : (
              <>
                <div><Label>Kapasitas Port</Label>
                  <Input type="number" min="1" max="256" value={form.kapasitas_port} onChange={set("kapasitas_port")} required data-testid="odp-form-kapasitas" /></div>
                <div><Label>Tanggal Pasang (opsional)</Label>
                  <Input type="date" value={form.tanggal_pasang} onChange={set("tanggal_pasang")} data-testid="odp-form-tanggal" /></div>
              </>
            )}
          </div>
          <div><Label>Alamat / Lokasi</Label>
            <Input value={form.alamat} onChange={set("alamat")} data-testid={`${kind}-form-alamat`} /></div>
          <CoordFields lat={form.latitude} lng={form.longitude} viewCenter={lok && lok.latitude != null && lok.longitude != null ? [lok.latitude, lok.longitude] : null}
                       onChange={(la, ln) => setForm((f) => ({ ...f, latitude: la, longitude: ln }))} />
          <div><Label>Catatan</Label>
            <Textarea rows={2} value={form.catatan} onChange={set("catatan")} data-testid={`${kind}-form-catatan`} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={saving} data-testid={`${kind}-form-submit`}>{saving ? "Menyimpan…" : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
